import React, { useEffect, useRef, useState } from 'react';
import { HeroSection } from '../components/HeroSection';
import { HeroSectionB } from '../components/HeroSectionB';
import { HeroSectionKit } from '../components/HeroSectionKit';
import { HeroSectionCards } from '../components/HeroSectionCards';
import { HeroSectionStory } from '../components/HeroSectionStory';
import { IncludesCarousel } from '../components/IncludesCarousel';
import { SignalSection } from '../components/SignalSection';
import { SymptomsSection } from '../components/SymptomsSection';
import { SignalBridge } from '../components/SignalBridge';
import { OfferSection } from '../components/OfferSection';
type DeferredProps = {
  load: () => Promise<any>;
  minHeight?: number;
  id?: string;
};
function DeferredSection({
  load,
  minHeight = 420,
  id
}: DeferredProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [Component, setComponent] = useState<React.ComponentType | null>(null);
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    let cancelled = false;
    const loadNow = () => {
      load().then(module => {
        if (!cancelled) {
          const exported = module.default ?? Object.values(module).find(value => typeof value === 'function');
          if (exported) setComponent(() => exported as React.ComponentType);
        }
      }).catch(() => {});
    };
    if (!('IntersectionObserver' in window)) {
      loadNow();
      return () => {
        cancelled = true;
      };
    }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        observer.disconnect();
        loadNow();
      }
    }, {
      rootMargin: '1000px 0px'
    });
    observer.observe(host);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [load]);
  return <div id={id} ref={hostRef} style={{
    minHeight: Component ? undefined : minHeight
  }} aria-busy={!Component} data-id="element-809" data-mp="1wxwrohqra2ok">
      {Component ? <Component data-id="element-810" data-mp="355st6od43rd8" /> : null}
    </div>;
}
const loadPain = () => import('../components/PainSection').then(m => ({
  default: m.PainSection
}));
const loadBenefits = () => import('../components/BenefitsSection').then(m => ({
  default: m.BenefitsSection
}));
const loadProtocol = () => import('../components/ProtocolSection').then(m => ({
  default: m.ProtocolSection
}));
const loadQuiz = () => import('../components/QuizSection').then(m => ({
  default: m.QuizSection
}));
const loadDifference = () => import('../components/DifferenceSection').then(m => ({
  default: m.DifferenceSection
}));
const loadTestimonials = () => import('../components/TestimonialsSection').then(m => ({
  default: m.TestimonialsSection
}));
const loadHowItWorks = () => import('../components/HowItWorksSection').then(m => ({
  default: m.HowItWorksSection
}));
const loadForWho = () => import('../components/ForWhoSection').then(m => ({
  default: m.ForWhoSection
}));
const loadIdentity = () => import('../components/IdentityTransitionSection').then(m => ({
  default: m.IdentityTransitionSection
}));
const loadOffer = () => import('../components/OfferSection').then(m => ({
  default: m.OfferSection
}));
const loadFAQ = () => import('../components/FAQSection').then(m => ({
  default: m.FAQSection
}));
const loadFinalCTA = () => import('../components/FinalCTASection').then(m => ({
  default: m.FinalCTASection
}));
export function LandingPage() {
  const params = typeof window === 'undefined' ? new URLSearchParams() : new URLSearchParams(window.location.search);
  // Hero por defecto: cards (Protocolo H.E.R. protagonista + carrusel "Todo lo que incluye H.E.R." que se mueve
  // solo), y "Tu cuerpo no falló" como sección quieta justo debajo.
  // Variantes: ?v=story (relato animado de ~6 s antes de H.E.R.), ?v=kit (composición estática),
  // ?v=a (hero original) y ?v=b (variante B).
  const variant = params.get('v') ?? 'cards';
  const previewPostPurchase = params.get('preview') === 'postpurchase';
  if (previewPostPurchase) {
    return <main className="min-h-screen bg-background text-foreground font-body" data-id="element-811" data-mp="3gq4w8pa36t1i"><OfferSection data-id="element-812" data-mp="3bsd6o1wwzj0h" /></main>;
  }
  const isStory = variant === 'story';
  const isTopHero = variant === 'cards' || variant === 'kit' || isStory;
  // Orden de las secciones (después del hero): Pain → Benefits → Método → Quiz → ¿En qué momento estás? (+ video)
  // → De la improvisación a una estrategia → Para quién → Identidad → Oferta → FAQ → Cierre.
  return <main className="overflow-hidden bg-background text-foreground min-h-screen font-body" data-id="element-813" data-mp="2y1zypzk0w4b5">
      {isStory && <HeroSectionStory data-id="element-814" data-mp="2t0alcd9bvejw" />}
      {isStory && <div className="bg-gradient-hero pt-8" data-id="element-815" data-mp="2o290pnw9776b"><IncludesCarousel data-id="element-816" data-mp="2o3afa2chabss" /></div>}
      {variant === 'cards' && <HeroSectionCards data-id="element-817" data-mp="2j3o2tv4h4zl1" />}
      {variant === 'kit' && <HeroSectionKit data-id="element-818" data-mp="2j4z67tu0c5bm" />}
      {!isStory && <SignalSection data-id="element-819" data-mp="mb5poxigbql8" />}
      {!isStory && <SymptomsSection data-id="element-820" data-mp="ma30qv4ovy7y" />}
      {!isStory && <SignalBridge data-id="element-821" data-mp="m91owofun7p6" />}
      {isTopHero ? null : variant === 'b' ? <HeroSectionB data-id="element-822" data-mp="hd486h0obzbv" /> : <HeroSection data-id="element-823" data-mp="hckq90b0xaxu" />}
      <DeferredSection load={loadPain} minHeight={420} data-id="element-824" data-mp="h9yhixcecpe1" />
      <DeferredSection load={loadTestimonials} minHeight={480} data-id="element-825" data-mp="ps0iyeeob3mp" />
      <DeferredSection load={loadBenefits} minHeight={360} data-id="element-826" data-mp="tunasfbcgojw" />
      <DeferredSection load={loadProtocol} minHeight={620} data-id="element-827" data-mp="tsu635kakeej" />
      <DeferredSection load={loadQuiz} minHeight={620} data-id="element-828" data-mp="yl3q64yq6k8w" />
      <DeferredSection load={loadHowItWorks} minHeight={480} data-id="element-829" data-mp="ynpxq4lm8zr4" />
      <DeferredSection load={loadDifference} minHeight={620} data-id="element-830" data-mp="13j5bkwjmivnj" />
      <DeferredSection load={loadForWho} minHeight={520} data-id="element-831" data-mp="6uh8d6ckhcy6" />
      <DeferredSection load={loadIdentity} minHeight={480} data-id="element-832" data-mp="6w0pez61rs10" />
      <DeferredSection load={loadOffer} minHeight={720} id="offer" data-id="element-833" data-mp="awua7jo70qpo" />
      <DeferredSection load={loadFAQ} minHeight={560} data-id="element-834" data-mp="fv5k1uehn17w" />
      <DeferredSection load={loadFinalCTA} minHeight={560} data-id="element-835" data-mp="ftb12lms1j6g" />
    </main>;
}